import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { t } from 'i18next';
import { Brush, Line, LineChart, ResponsiveContainer } from 'recharts';
import { MarsinetContext } from '../../context/marsinetProvider';
import { formatUtcTimestamp } from '../../utils/timezone-utils';

interface BrushGraphsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  startIndex: number;
  endIndex: number;
  onBrushChange: (range: { startIndex: number; endIndex: number }) => void;
  alarms?: { timestamp: string | number; event_type: string | number }[];
  selectedAlarm?: { timestamp: string | number; event_id: string };
}

const BrushGraphs: React.FC<BrushGraphsProps> = ({
  data,
  startIndex,
  endIndex,
  onBrushChange,
  alarms,
  selectedAlarm,
}) => {
  const brushRef = useRef<HTMLDivElement>(null);
  const [brushWidth, setBrushWidth] = useState<number | null>(null);
  const { selectedTimezone } = useContext(MarsinetContext);
  const locale = navigator.language || 'en-US';

  // Los límites del selector son instantes UTC, pero las etiquetas usan la zona elegida.
  const formatTime = (timestamp?: string) =>
    timestamp
      ? formatUtcTimestamp(timestamp, selectedTimezone, locale, {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      : '--';

  const selectedRange = useMemo(() => {
    if (!data || data.length === 0) {
      return { start: undefined, end: undefined };
    }
    return {
      start: data[0]?.timestamp,
      end: data[data.length - 1]?.timestamp,
    };
  }, [data]);

  // Update brush width on resize
  useEffect(() => {
    if (!brushRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setBrushWidth(entry.contentRect.width);
    });
    ro.observe(brushRef.current);
    return () => ro.disconnect();
  }, []);

  // Scroll event to handle sticky brush
  if (!data || data.length === 0) {
    return (
      <div className="mt-4 text-center text-gray-500 text-sm">
        {t('emptyMessage')}
      </div>
    );
  }

  return (
    <div>
      <div ref={brushRef} className="mt-4">
        <div className="flex flex-wrap items-start space-y-2 text-blue-800 text-sm font-bold ml-2">
          <div className="flex items-center ml-2">
            <i className="pi pi-clock mr-2" />
            <span className="mr-5">
              {`Start: ${formatTime(selectedRange.start)}`}
            </span>
          </div>
          <div className="flex items-center">
            <i className="pi pi-clock mr-2" />
            <span>
              {`End: ${formatTime(selectedRange.end)}`}
            </span>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={60}>
          <LineChart data={data} syncId="brushId">
            <Line
              key={1}
              type="monotone"
              dataKey="value"
              strokeWidth={2}
              dot={false}
              connectNulls
              stroke="#004e78"
            />
            <Brush
              dataKey="timestamp"
              height={30}
              stroke="#004e78"
              fill="#E6F2FF"
              travellerWidth={10}
              traveller={(props) => (
                <rect {...props} fill="#13dfb6" stroke="#004e78" rx={3} />
              )}
              startIndex={startIndex}
              endIndex={endIndex}
              onChange={onBrushChange}
              tickFormatter={(value) => value}
            >
              <g>
                {!selectedAlarm &&
                  alarms?.map((alarm) => {
                    const alarmIndex = data.findIndex(
                      (p) => p.timestamp >= alarm.timestamp
                    );
                    if (alarmIndex < 0 || alarmIndex > data.length - 1)
                      return null;
                    const position = (alarmIndex / data.length) * brushWidth;
                    return (
                      <rect
                        key={String(alarm.timestamp)}
                        x={position}
                        y={25}
                        width={0.5}
                        height={30}
                        stroke={Number(alarm.event_type) !== 2 ? 'red' : 'blue'}
                        strokeWidth="0.6"
                        fill="none"
                      />
                    );
                  })}
              </g>
            </Brush>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BrushGraphs;
